import { type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { COMPANY_TABS } from './companyTabs';

/**
 * CompanyLayout — обёртка раздела «Управление компанией» (AK-350).
 *
 * AppShell + единая шапка + горизонтальная лента вкладок (deep-link через URL:
 * /company/<key>). Активная вкладка приходит пропсом `activeTab` (сегмент URL),
 * `null` — обзорный лендинг /company.
 *
 * Мобайл: лента вкладок в overflow-x скролле (не обрезается, не ломает layout).
 * Вкладки — `<Link>`, а не JS-state: F5 / шаринг / «назад» сохраняют вкладку.
 */
export function CompanyLayout({
  activeTab,
  children,
}: {
  /** Ключ активной вкладки (structure|team|roles|reference|integrations|profile) или null для лендинга. */
  activeTab: string | null;
  children: ReactNode;
}) {
  return (
    <AppShell>
      {/* pb-safe-fab — clearance под плавающий ФАБ «ИИ» + safe-area (AK-374). */}
      <div className="mx-auto max-w-6xl pb-safe-fab">
        <PageHeader
          title="Управление компанией"
          showBack={false}
          description="Хозяйства, люди, доступы и реквизиты — в одном месте."
        />

        {/* Горизонтальные вкладки — сегмент URL. overflow-x на мобайле. */}
        <nav
          className="mt-4 -mb-px flex gap-1 overflow-x-auto border-b border-border"
          aria-label="Разделы компании"
        >
          {COMPANY_TABS.map((tab) => {
            const active = tab.key === activeTab;
            const Icon = tab.icon;
            return (
              <Link
                key={tab.key}
                to="/company/$tab"
                params={{ tab: tab.key }}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.soon && (
                  <Badge
                    variant="secondary"
                    className="px-2 py-0 text-xs font-medium"
                  >
                    Скоро
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="pt-5">{children}</div>
      </div>
    </AppShell>
  );
}

export default CompanyLayout;
